/**
 * O4 — URL-query-param parser for the OBS overlay route.
 *
 * Maps a `URLSearchParams` (or any `string → string` lookup) onto a
 * structured `OverlayRouteParams` config the `<OverlayRoute>` reads.
 *
 * Per `commercial/01-frontend-integration.md §8 line 214`: streamers
 * configure which panels show + which background mode via query
 * params. The OBS Browser Source URL is what the streamer pastes; we
 * parse it here.
 *
 * Examples:
 *   ?panels=metadata,statA,reasoningA&bg=chromakey
 *   ?panels=telemetry&bg=transparent&reasoningStats=1
 *   ?match=/replays/match-123/battle_0.jsonl&transcriptA=/t/a.jsonl
 *
 * Unknown panel names are silently dropped (forward-compat). Empty or
 * absent `panels` falls back to the caller's default. Unrecognized
 * `bg` values default to `"normal"`.
 */

import type { OverlayPanel } from "./MatchOverlay";

const KNOWN_PANELS: ReadonlySet<OverlayPanel> = new Set<OverlayPanel>([
  "metadata",
  "statA",
  "statB",
  "reasoningA",
  "reasoningB",
  "telemetry",
]);

export type OverlayBackground = "normal" | "chromakey" | "transparent";
const KNOWN_BACKGROUNDS: ReadonlySet<OverlayBackground> = new Set([
  "normal",
  "chromakey",
  "transparent",
]);

export interface OverlayRouteParams {
  /** Panels to mount. `null` ⇒ caller's default applies. */
  panels: ReadonlyArray<OverlayPanel> | null;
  /** Background mode. */
  background: OverlayBackground;
  /** When true, show LLM stats (model / tokens / latency). */
  reasoningStats: boolean;
  /** Show numeric legend under each side's zone armor diagram. */
  zoneLegend: boolean;
  /** Override the side stat panel maxRows for telemetry. `null` ⇒
   *  caller's default applies. */
  telemetryMaxRows: number | null;
  /** Battle JSONL URL (single-battle replay archive). The route
   *  fetches + parses this. */
  matchUrl: string | null;
  /** Optional per-side transcript JSONL URLs (gzipped JSONL — the
   *  route decompresses + parses upstream of MatchOverlay). */
  transcriptUrlA: string | null;
  transcriptUrlB: string | null;
  /** Signed-URL token for the Researcher API. When set, the route
   *  forwards it as a Bearer header to the transcript fetches.
   *  Streamers paste signed URLs (COM-11). */
  authToken: string | null;
}

const DEFAULTS: OverlayRouteParams = {
  panels: null,
  background: "normal",
  reasoningStats: false,
  zoneLegend: false,
  telemetryMaxRows: null,
  matchUrl: null,
  transcriptUrlA: null,
  transcriptUrlB: null,
  authToken: null,
};

/** Source signature — any object that supports `.get(key)`. */
export interface ParamSource {
  get(key: string): string | null;
}

/**
 * Parse a `URLSearchParams`-shaped source. Returns a fully-populated
 * config; missing keys take defaults so the consumer doesn't need
 * to guard each field individually.
 */
export function parseOverlayParams(src: ParamSource): OverlayRouteParams {
  return {
    panels: parsePanels(src.get("panels")),
    background: parseBackground(src.get("bg")),
    reasoningStats: parseBool(src.get("reasoningStats")),
    zoneLegend: parseBool(src.get("zoneLegend")),
    telemetryMaxRows: parseNullableInt(src.get("telemetryMaxRows")),
    matchUrl: nonEmpty(src.get("match")),
    transcriptUrlA: nonEmpty(src.get("transcriptA")),
    transcriptUrlB: nonEmpty(src.get("transcriptB")),
    authToken: nonEmpty(src.get("token")),
  };
}

/** Convenience: parse a full URL or query string. */
export function parseOverlayParamsString(query: string): OverlayRouteParams {
  const trimmed = query.startsWith("?") ? query.slice(1) : query;
  return parseOverlayParams(new URLSearchParams(trimmed));
}

function parsePanels(raw: string | null): ReadonlyArray<OverlayPanel> | null {
  if (!raw) return null;
  const parts = raw
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length === 0) return null;
  const accepted: OverlayPanel[] = [];
  for (const p of parts) {
    if (KNOWN_PANELS.has(p as OverlayPanel)) accepted.push(p as OverlayPanel);
  }
  return accepted.length > 0 ? accepted : null;
}

function parseBackground(raw: string | null): OverlayBackground {
  if (raw && KNOWN_BACKGROUNDS.has(raw as OverlayBackground)) {
    return raw as OverlayBackground;
  }
  return DEFAULTS.background;
}

function parseBool(raw: string | null): boolean {
  if (!raw) return false;
  const v = raw.toLowerCase();
  return v === "1" || v === "true" || v === "yes" || v === "on";
}

function parseNullableInt(raw: string | null): number | null {
  if (!raw) return null;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function nonEmpty(raw: string | null): string | null {
  return raw && raw.length > 0 ? raw : null;
}
